import { useEffect, useRef, useState } from "react";
import { BatteryCharging, TimerReset, Timer } from "lucide-react";
import { toast } from "sonner";

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export const ChargeTimer = ({ trip, day, stop, stopIndex }) => {
  const key = `vr-charge-timer-${trip.id}-${day.day_number}-${stopIndex}`;
  const [startTs, setStartTs] = useState(() => parseInt(localStorage.getItem(key), 10) || null);
  const [, setTick] = useState(0);
  const doneRef = useRef(false);
  const total = (stop.charge_minutes || 0) * 60;

  useEffect(() => {
    if (!startTs) return;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [startTs]);

  const elapsed = startTs ? Math.floor((Date.now() - startTs) / 1000) : 0;
  const remain = Math.max(total - elapsed, 0);
  const frac = startTs ? Math.min(elapsed / total, 1) : 0;
  const fromSoc = stop.arrival_soc ?? 15;
  const toSoc = stop.charge_to_soc || 80;
  const socNow = Math.round(fromSoc + frac * (toSoc - fromSoc));

  useEffect(() => {
    if (!startTs || remain > 0 || doneRef.current) return;
    doneRef.current = true;
    if (navigator.vibrate) navigator.vibrate([300, 150, 300, 150, 300]);
    toast.success(`Batterij ~${toSoc}% — klaar om verder te rijden!`, { duration: 30000 });
  }, [startTs, remain, toSoc]);

  if (!total) return null;

  const start = () => {
    const ts = Date.now();
    doneRef.current = false;
    localStorage.setItem(key, String(ts));
    setStartTs(ts);
  };
  const reset = () => {
    localStorage.removeItem(key);
    doneRef.current = false;
    setStartTs(null);
  };

  if (!startTs) {
    return (
      <button data-testid="charge-timer-start" onClick={start}
        className="mt-4 w-full rounded-full border border-[#52B788]/40 text-[#52B788] hover:bg-[#52B788]/10 text-sm font-semibold py-2.5 flex items-center justify-center gap-2 transition-[background-color] duration-200">
        <Timer className="w-4 h-4" /> Start laad-timer (±{stop.charge_minutes} min)
      </button>
    );
  }

  return (
    <div data-testid="charge-timer" className="mt-4 rounded-xl bg-[#52B788]/10 border border-[#52B788]/30 p-4">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase font-semibold text-[#52B788]">
          <BatteryCharging className={`w-4 h-4 ${remain > 0 ? "animate-pulse" : ""}`} /> {remain > 0 ? "Aan het laden" : "Klaar!"}
        </span>
        <button data-testid="charge-timer-reset" onClick={reset} title="Timer stoppen"
          className="p-1.5 rounded-full hover:bg-secondary text-muted-foreground transition-[background-color] duration-200">
          <TimerReset className="w-4 h-4" />
        </button>
      </div>
      <div className="flex items-end justify-between mt-2">
        <span data-testid="charge-timer-remaining" className="font-heading font-extrabold text-3xl tracking-tight">
          {remain > 0 ? fmt(remain) : "0:00"}
        </span>
        <span data-testid="charge-timer-soc" className="font-mono text-sm text-[#52B788] font-bold">nu ~{socNow}% → {toSoc}%</span>
      </div>
      <div className="h-2 rounded-full bg-secondary mt-2.5 overflow-hidden">
        <div className="h-full rounded-full bg-[#52B788] transition-[width] duration-1000" style={{ width: `${Math.round(frac * 100)}%` }} />
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        {remain > 0 ? `Rond ${new Date(startTs + total * 1000).toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" })} zit je batterij theoretisch op ~${toSoc}%.` : `Batterij theoretisch op ~${toSoc}% — goede reis verder!`}
      </p>
    </div>
  );
};
